import { Component, ComponentChildren } from 'preact';
import { AlertTriangle, RefreshCw } from 'lucide-preact';

interface ErrorBoundaryProps {
  children: ComponentChildren;
  fallback?: ComponentChildren;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: any) {
    console.error('[ErrorBoundary] Erro não capturado na interface Preact:', error, errorInfo);
  }

  private handleReload = () => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div class="h-screen w-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-center text-slate-100">
          <div class="w-16 h-16 rounded-2xl bg-red-500/10 border border-red-500/30 flex items-center justify-center mb-4 text-red-400">
            <AlertTriangle class="w-8 h-8" />
          </div>
          <h2 class="text-xl font-bold text-slate-100 mb-2">Ops! Algo deu errado na interface.</h2>
          <p class="text-xs text-slate-300 max-w-md mb-6 leading-relaxed">
            Ocorreu uma falha inesperada durante a renderização. Você pode tentar recarregar a interface para restaurar o estado.
          </p>
          {this.state.error?.message && (
            <div class="w-full max-w-md p-3 mb-6 bg-slate-900 border border-slate-800 rounded-xl text-left text-xs font-mono text-red-300/90 overflow-x-auto">
              {this.state.error.message}
            </div>
          )}
          <button
            onClick={this.handleReload}
            class="px-5 py-2.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded-xl font-bold text-xs flex items-center gap-2 transition shadow-lg cursor-pointer"
          >
            <RefreshCw class="w-4 h-4" />
            <span>Recarregar Aplicação</span>
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
